import { FC, SyntheticEvent, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmptyState from "../../../../components/ui/EmptyState";
import { FrequentlyQuestion } from "../../../../models/frequently-question.model";

type Props = {
  items: FrequentlyQuestion[];
  onClearSearch: () => void;
};

/** Accordion de perguntas frequentes. */
const SupportFaq: FC<Props> = ({ items, onClearSearch }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      sx={{
        maxWidth: 860,
        mx: "auto",
        backgroundColor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "16px",
        p: { xs: "20px 16px", md: "28px 32px" },
        mb: 4,
      }}
    >
      <Typography sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: 23, color: "text.primary", mb: 2.5 }}>
        Perguntas frequentes
      </Typography>

      {items.length === 0 ? (
        <EmptyState title="Nenhum resultado" description="Nenhuma pergunta encontrada para essa busca." actionLabel="Limpar busca" onAction={onClearSearch} />
      ) : (
        items.map((item, index) => (
          <Accordion
            key={index}
            expanded={expanded === `${index}`}
            onChange={handleChange(`${index}`)}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              border: "none",
              borderBottom: `1px solid ${theme.palette.divider}`,
              "&:last-of-type": { borderBottom: "none" },
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ color: "text.secondary", transition: "transform 0.2s", transform: expanded === `${index}` ? "rotate(180deg)" : "rotate(0deg)" }} />
              }
              sx={{ px: 0, py: 1.5, minHeight: "unset", "& .MuiAccordionSummary-content": { my: 0 } }}
            >
              <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "text.primary" }}>{item.question}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pb: 2, pt: 0 }}>
              {item.answers.map((answer, i) => (
                <Typography key={i} sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.7, mb: i < item.answers.length - 1 ? 0.5 : 0 }}>
                  {answer}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default SupportFaq;
